import React from 'react';

import ApplicationJourneyStepLayout from '../../ApplicationJourneyStepLayout';
import ApplicationJourneySubStepProgress from '../../ApplicationJourneySubStepProgress';
import {
  ErrorBanner,
  JourneyActionBar,
  StatusMessage,
} from '../../shared/components';

import {lshUseCompanyDetailsStep} from './useCompanyDetailsStep';
import {
  BankingSubStep,
  BbbeeSubStep,
  ConfirmDetailsSubStep,
  RegistrationSubStep,
  RelatedPartiesSubStep,
} from './substeps';

const CompanyDetailsStepContainer: React.FC = () => {
  const {
    application,
    traderApplication,
    activeStep,
    companySubSteps,
    activeSubStep,
    activeSubStepIndex,
    activeSubStepKey,
    goPrevious,
    goToSubStep,
    registrationFieldLabel,
    registrationError,
    registrationNumber,
    companyType,
    companyDetailsSection,
    detailsFormRef,
    detailsError,
    submitDetailsChanges,
    bbbeeOptions,
    bbbeeLevel,
    bankInputs,
    bankNameOptions,
    accountTypeOptions,
    relatedParties,
    busyReportId,
    isRefreshing,
    isSubmitting,
    error,
    notice,
    handleRefresh,
    handleNext,
    handleRegistrationChange,
    handleBankInputChange,
    handleBbbeeLevelChange,
    handleDownloadPartyCreditReport,
  } = lshUseCompanyDetailsStep();

  const renderSubStepContent = () => {
    if (activeSubStepKey === 'registration') {
      return (
        <RegistrationSubStep
          registrationFieldLabel={registrationFieldLabel}
          registrationError={registrationError}
          registrationNumber={registrationNumber}
          onRegistrationChange={handleRegistrationChange}
        />
      );
    }

    if (activeSubStepKey === 'details') {
      return (
        <ConfirmDetailsSubStep
          companyDetailsSection={companyDetailsSection}
          detailsFormRef={detailsFormRef}
          submitDetailsChanges={submitDetailsChanges}
          isSubmitting={isSubmitting}
          detailsError={detailsError}
          companyType={companyType}
        />
      );
    }

    if (activeSubStepKey === 'parties') {
      return (
        <RelatedPartiesSubStep
          relatedParties={relatedParties}
          busyReportId={busyReportId}
          onDownloadPartyCreditReport={handleDownloadPartyCreditReport}
        />
      );
    }

    if (activeSubStepKey === 'bbbee') {
      return (
        <BbbeeSubStep
          bbbeeOptions={bbbeeOptions}
          bbbeeLevel={bbbeeLevel}
          onBbbeeLevelChange={handleBbbeeLevelChange}
        />
      );
    }

    return (
      <BankingSubStep
        bankNameOptions={bankNameOptions}
        accountTypeOptions={accountTypeOptions}
        bankInputs={bankInputs}
        onBankInputChange={handleBankInputChange}
      />
    );
  };

  return (
    <>
      <ApplicationJourneySubStepProgress
        steps={companySubSteps.map(({key, title}) => ({key, title}))}
        activeIndex={activeSubStepIndex}
        ariaLabel="Company profile steps"
        onStepClick={goToSubStep}
      />
      <ApplicationJourneyStepLayout
        title={activeSubStep?.title || activeStep.title}
        description={activeSubStep?.description || activeStep.description}
      >
        <div className="company-step-toolbar company-step-toolbar--actions">
          <button
            type="button"
            className="link-button"
            onClick={() => {
              void handleRefresh();
            }}
            disabled={isRefreshing || isSubmitting}
          >
            {isRefreshing
              ? 'Refreshing…'
              : isSubmitting
              ? 'Saving…'
              : 'Refresh data'}
          </button>
        </div>

        {!application && !traderApplication ? (
          <div className="application-empty-state">
            <h3>No company profile available</h3>
            <p>
              We could not prepare the supplier company details for this
              application yet. Refresh the journey to try again.
            </p>
          </div>
        ) : (
          <>
            {error ? <ErrorBanner message={error} /> : null}
            {!error && notice ? <StatusMessage message={notice} /> : null}

            {renderSubStepContent()}

            <JourneyActionBar
              className="company-step-nav"
              secondaryAction={{
                label: 'Back',
                onClick: goPrevious,
                disabled: isSubmitting,
              }}
              primaryAction={{
                label: 'Save & Continue',
                onClick: () => {
                  void handleNext();
                },
                disabled: isSubmitting,
              }}
            />
          </>
        )}
      </ApplicationJourneyStepLayout>
    </>
  );
};

export default CompanyDetailsStepContainer;
